import { Component, OnInit, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import { VehicleService } from '../../../core/services/vehicle.service';
import { PassengerNextStop, PassengerRouteStop, PassengerTripMapItem, VehicleTrackingService } from '../../../core/services/vehicle-tracking.service';
import { Subscription } from 'rxjs';
import { VehicleTracking } from '../../../core/models/vehicle-tracking.model';
import { AuthService } from '../../../core/services/auth.service';
import { normalizeUserRole, UserRole } from '../../../core/enums/user-role.enum';
import { ActivatedRoute } from '@angular/router';

type VehicleMapStatus = 'moving' | 'idle' | 'stale' | 'noSignal';

@Component({
  selector: 'app-map',
  standalone: false,
  templateUrl: './map.component.html',
  styleUrl: './map.component.css'
})
export class MapComponent implements OnInit, OnDestroy {
  private map!: L.Map;
  private markers: L.Marker[] = [];
  private routeLayers: L.Layer[] = [];
  private vehicleMarkers = new Map<number, L.Marker>(); // Track markers by vehicle ID
  private vehicleStatuses = new Map<number, VehicleMapStatus>();
  private updateSubscription!: Subscription;
  private vehicles: any[] = [];
  isPassengerMap = false;
  passengerTrips: PassengerTripMapItem[] = [];
  selectedPassengerTrip: PassengerTripMapItem | null = null;
  searchTerm = '';
  statusFilter = 'all';
  isFullscreen = false;
  isLoading = true;
  trackingError = '';
  lastUpdated: Date | null = null;
  selectedVehicle: any | null = null;
  selectedTracking: VehicleTracking | null = null;
  fleetSummary = {
    total: 0,
    moving: 0,
    idle: 0,
    stale: 0,
    noSignal: 0
  };
  passengerHighlights = [
    { label: 'Next arrival', value: 'Calculating', icon: 'schedule' },
    { label: 'Closest stop', value: 'Select route', icon: 'pin_drop' },
    { label: 'Trip status', value: 'Live tracking', icon: 'near_me' }
  ];

  constructor(
    private vehicleService: VehicleService,
    private trackingService: VehicleTrackingService,
    private authService: AuthService,
    private route: ActivatedRoute
  ) { }
  
  ngOnInit(): void {
    this.initMap();
    document.addEventListener('fullscreenchange', this.syncFullscreenState);
    this.isPassengerMap = normalizeUserRole(this.authService.getCurrentUser()?.userRoleName || this.authService.getCurrentUser()?.userRole || this.authService.getCurrentUser()?.userRoleID) === UserRole.PASSENGER;

    if (this.isPassengerMap) {
      this.loadPassengerTrips();
      return;
    }

    this.loadVehiclesAndStartUpdates();
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }

    document.removeEventListener('fullscreenchange', this.syncFullscreenState);
  }

  private initMap(): void {
    this.map = L.map('map').setView([-30.5595, 22.9375], 6);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    setTimeout(() => this.map.invalidateSize(), 0);
  }

  private loadVehiclesAndStartUpdates(): void {
    this.isLoading = true;
    this.trackingError = '';

    this.vehicleService.getVehicles().subscribe({
      next: vehicles => {
        this.vehicles = vehicles;
        this.fleetSummary = {
          total: vehicles.length,
          moving: 0,
          idle: 0,
          stale: 0,
          noSignal: vehicles.length
        };

        const vehicleIds = vehicles.map(vehicle => vehicle.vehicleID).filter(Boolean);

        if (!vehicleIds.length) {
          this.isLoading = false;
          return;
        }

        this.updateSubscription = this.trackingService.startTrackingPolling(vehicleIds, 30000).subscribe({
          next: trackingMap => this.updateVehiclesFromTrackingMap(trackingMap),
          error: error => {
            console.error('Live tracking polling failed:', error);
            this.trackingError = 'Unable to load live tracking from the Fleet Tracking API.';
            this.isLoading = false;
          }
        });
      },
      error: error => {
        console.error('Vehicle loading failed:', error);
        this.trackingError = 'Unable to load vehicles from the Fleet Tracking API.';
        this.isLoading = false;
      }
    });
  }

  private loadPassengerTrips(): void {
    this.isLoading = true;
    this.trackingError = '';
    this.clearMapLayers();

    const bookingId = Number(this.route.snapshot.queryParamMap.get('bookingId') || 0) || null;
    const tripId = Number(this.route.snapshot.queryParamMap.get('tripId') || 0) || null;

    this.trackingService.getPassengerTripMap({ bookingId, tripId }).subscribe({
      next: trips => {
        this.passengerTrips = trips;
        this.vehicles = trips.map(item => this.mapPassengerVehicle(item)).filter(Boolean);
        this.fleetSummary = this.createPassengerSummary(trips);
        this.selectedPassengerTrip = this.getDefaultPassengerTrip(trips);

        if (!trips.length) {
          this.trackingError = 'You do not have booked trips to track yet.';
          this.isLoading = false;
          return;
        }

        trips.forEach(item => this.renderPassengerTrip(item));

        if (this.selectedPassengerTrip) {
          this.selectPassengerTrip(this.selectedPassengerTrip);
        } else {
          this.showAllVehicles();
        }

        this.lastUpdated = new Date();
        this.isLoading = false;
        this.loadSelectedPastTripHistory();
      },
      error: error => {
        console.error('Passenger trip map loading failed:', error);
        this.trackingError = 'Unable to load your booked trip map right now.';
        this.isLoading = false;
      }
    });
  }

  private loadSelectedPastTripHistory(): void {
    if (!this.selectedPassengerTrip?.trip?.isPast) {
      return;
    }

    this.trackingService.getPassengerTripMap({
      bookingId: this.selectedPassengerTrip.bookingID,
      tripId: this.selectedPassengerTrip.trip?.tripID,
      includeHistory: true,
      historyTake: 250
    }).subscribe({
      next: trips => {
        const refreshedTrip = trips[0];
        if (!refreshedTrip) {
          return;
        }

        this.selectedPassengerTrip = refreshedTrip;
        const index = this.passengerTrips.findIndex(item => item.bookingID === refreshedTrip.bookingID);
        if (index >= 0) {
          this.passengerTrips[index] = refreshedTrip;
        }
        this.renderPassengerTrip(refreshedTrip, true);
      },
      error: error => console.error('Passenger trip history loading failed:', error)
    });
  }

  private updateVehiclesFromTrackingMap(trackingMap: Map<number, VehicleTracking>): void {
    const summary = {
      total: this.vehicles.length,
      moving: 0,
      idle: 0,
      stale: 0,
      noSignal: 0
    };

    this.vehicles.forEach(vehicle => {
      const tracking = trackingMap.get(vehicle.vehicleID);

      if (!tracking || !this.hasValidCoordinates(tracking)) {
        this.vehicleStatuses.set(vehicle.vehicleID, 'noSignal');
        summary.noSignal++;
        return;
      }

      const status = this.getTrackingStatus(tracking);
      this.vehicleStatuses.set(vehicle.vehicleID, status);
      summary[status]++;
      this.updateOrCreateMarker(vehicle, tracking, status);
    });

    this.fleetSummary = summary;
    this.lastUpdated = new Date();
    this.isLoading = false;
  }

  private updateOrCreateMarker(vehicle: any, tracking: VehicleTracking, status: VehicleMapStatus): void {
    const vehicleIcon = this.createVehicleIcon(status);

    // Check if marker already exists for this vehicle
    if (this.vehicleMarkers.has(vehicle.vehicleID)) {
      // Update existing marker position
      const marker = this.vehicleMarkers.get(vehicle.vehicleID)!;
      marker.setLatLng(this.getLatLng(tracking));
      marker.setIcon(vehicleIcon);
      
      // Update popup content
      const popupContent = this.createPopupContent(vehicle, tracking);
      marker.setPopupContent(popupContent);
      if (this.selectedVehicle?.vehicleID === vehicle.vehicleID) {
        this.selectedTracking = tracking;
      }
    } else {
      // Create new marker
      const marker = L.marker(
        this.getLatLng(tracking),
        { icon: vehicleIcon }
      );
      
      const popupContent = this.createPopupContent(vehicle, tracking);
      marker.bindPopup(popupContent);
      marker.addTo(this.map);
      marker.on('click', () => {
        this.selectedVehicle = vehicle;
        this.selectedTracking = tracking;
      });
      
      // Store marker
      this.vehicleMarkers.set(vehicle.vehicleID, marker);
      this.markers.push(marker);
    }
  }

  private renderPassengerTrip(item: PassengerTripMapItem, refreshOnly = false): void {
    if (refreshOnly) {
      this.clearRouteLayers();
    }

    const tracking = item.currentTracking;
    const vehicle = this.mapPassengerVehicle(item);
    const history = this.asArray<VehicleTracking>(item.routeHistory).filter(point => this.hasValidCoordinates(point));

    this.renderPlannedRoute(item);

    if (item.trip?.isPast && history.length) {
      this.addPolyline(history.map(point => this.getLatLng(point)), '#4b1305', 'Travelled route');
    }

    if (!tracking || !this.hasValidCoordinates(tracking) || !vehicle) {
      if (vehicle?.vehicleID) {
        this.vehicleStatuses.set(vehicle.vehicleID, 'noSignal');
      }
      return;
    }

    const status = this.getTrackingStatus(tracking);
    this.vehicleStatuses.set(vehicle.vehicleID, status);
    this.updateOrCreateMarker(vehicle, tracking, status);
  }

  private renderPlannedRoute(item: PassengerTripMapItem): void {
    const stops = this.getOrderedStops(item);
    const stopPoints = stops.filter(stop => this.hasStopCoordinates(stop));

    stopPoints.forEach(stop => {
      const marker = L.circleMarker([Number(stop.stopLatitude), Number(stop.stopLongitude)], {
        radius: 5,
        color: '#4b1305',
        fillColor: '#f6c21a',
        fillOpacity: 0.95,
        weight: 2
      }).bindPopup(`<strong>${stop.stopName || 'Stop'}</strong><br>Stop ${stop.stopOrder || ''}`);
      marker.addTo(this.map);
      this.routeLayers.push(marker);
    });

    if (stopPoints.length > 1) {
      this.addPolyline(stopPoints.map(stop => [Number(stop.stopLatitude), Number(stop.stopLongitude)]), '#64748b', 'Planned route', true);
    }
  }

  private addPolyline(points: L.LatLngExpression[], color: string, label: string, dashed = false): void {
    const line = L.polyline(points, {
      color,
      dashArray: dashed ? '6 8' : undefined,
      opacity: dashed ? 0.7 : 0.9,
      weight: dashed ? 3 : 4
    }).bindPopup(label);
    line.addTo(this.map);
    this.routeLayers.push(line);
  }

  private createPopupContent(vehicle: any, tracking: VehicleTracking): string {
    return `
      <div style="min-width: 200px;">
        <strong>${vehicle.vehicleRegistration}</strong><br>
        ${[vehicle.vehicleLabel, vehicle.vehicleManufacturerMake, vehicle.vehicleManufacturerModel].filter(Boolean).join(' ')}<br>
        <hr>
        <strong>Status:</strong><br>
        Speed: ${Number(tracking.vehicleTrackingSpeed || 0).toFixed(2)} km/h<br>
        Altitude: ${Number(tracking.vehicleTrackingAltitude || 0).toFixed(1)} m<br>
        Satellites: ${tracking.vehicleTrackingSatelites}<br>
        Updated: ${new Date(tracking.vehicleTrackingDateTime).toLocaleTimeString()}<br>
        <hr>
        <small>VIN: ${vehicle.vehicleIdentificationNumber}</small>
      </div>
    `;
  }

  // Method to fit map to show all vehicles
  showAllVehicles(): void {
    if (this.markers.length > 0) {
      const bounds = L.latLngBounds(
        this.markers.map(marker => marker.getLatLng())
      );
      this.map.fitBounds(bounds);
      return;
    }

    const routeBounds = this.getRouteLayerBounds();
    if (routeBounds) {
      this.map.fitBounds(routeBounds);
    }
  }

  toggleFullscreen(): void {
    const container = document.querySelector('.map-container') as HTMLElement | null;

    if (!container) {
      return;
    }

    this.isFullscreen = !this.isFullscreen;

    if (this.isFullscreen && !document.fullscreenElement && container.requestFullscreen) {
      container.requestFullscreen().catch(() => this.invalidateMapSize());
    }

    if (!this.isFullscreen && document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => this.invalidateMapSize());
    }

    this.invalidateMapSize();
  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    if (this.isPassengerMap) {
      const trip = this.passengerTrips.find(item => item.vehicle?.vehicleID === vehicle.vehicleID) || null;
      if (trip) {
        this.selectedPassengerTrip = trip;
        this.selectedTracking = trip.currentTracking || null;
      }
    }
    const marker = this.vehicleMarkers.get(vehicle.vehicleID);

    if (marker) {
      marker.openPopup();
      this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 13));
    }
  }

  selectPassengerTrip(item: PassengerTripMapItem): void {
    this.selectedPassengerTrip = item;
    const vehicle = this.mapPassengerVehicle(item);
    if (vehicle) {
      this.selectVehicle(vehicle);
    }

    if (!item.currentTracking && this.getOrderedStops(item).length) {
      const stopPoints = this.getOrderedStops(item).filter(stop => this.hasStopCoordinates(stop));
      if (stopPoints.length) {
        this.map.fitBounds(L.latLngBounds(stopPoints.map(stop => [Number(stop.stopLatitude), Number(stop.stopLongitude)])));
      }
    }
  }

  getFilteredVehicles(): any[] {
    const search = this.searchTerm.trim().toLowerCase();

    return this.vehicles.filter(vehicle => {
      const label = [
        vehicle.vehicleRegistration,
        vehicle.vehicleLabel,
        vehicle.vehicleManufacturerMake,
        vehicle.vehicleManufacturerModel
      ].filter(Boolean).join(' ').toLowerCase();

      const matchesSearch = !search || label.includes(search);
      const status = this.vehicleStatuses.get(vehicle.vehicleID);
      const matchesStatus = this.statusFilter === 'all' || status === this.statusFilter;

      return matchesSearch && matchesStatus;
    });
  }

  getSelectedVehicleTitle(): string {
    if (!this.selectedVehicle) {
      return 'No vehicle selected';
    }

    return this.selectedVehicle.vehicleRegistration || this.selectedVehicle.vehicleLabel || `Vehicle ${this.selectedVehicle.vehicleID}`;
  }

  getMapHeading(): string {
    return this.isPassengerMap ? 'Trip Map' : 'Operations Map';
  }

  getMapEyebrow(): string {
    return this.isPassengerMap ? 'Passenger trip tracking' : 'Live fleet monitor';
  }

  getMapToolbarTitle(): string {
    return this.isPassengerMap ? 'Your booked trip map' : 'South Africa fleet view';
  }

  getSelectedVehicleSubtitle(): string {
    if (!this.selectedVehicle) {
      return 'Choose a vehicle from the fleet list or click a marker.';
    }

    return [
      this.selectedVehicle.vehicleLabel,
      this.selectedVehicle.vehicleManufacturerMake,
      this.selectedVehicle.vehicleManufacturerModel
    ].filter(Boolean).join(' ') || 'Fleet vehicle';
  }

  getVehicleStatus(vehicle: any): VehicleMapStatus {
    return this.vehicleStatuses.get(vehicle.vehicleID) || 'noSignal';
  }

  getVehicleStatusLabel(vehicle: any): string {
    const status = this.getVehicleStatus(vehicle);
    const labels: Record<VehicleMapStatus, string> = {
      moving: 'Moving',
      idle: 'Idle',
      stale: 'Stale GPS',
      noSignal: 'No signal'
    };

    return labels[status];
  }

  getPassengerTripTitle(item: PassengerTripMapItem | null): string {
    return item?.trip?.tripName || item?.route?.routeName || 'Booked trip';
  }

  getPassengerOperator(item: PassengerTripMapItem | null): string {
    return item?.operator?.orgName || item?.operator?.operatorCode || 'Operator';
  }

  getPassengerRouteName(item: PassengerTripMapItem | null): string {
    return item?.route?.routeName || item?.trip?.tripName || 'Route';
  }

  getPassengerTripTime(item: PassengerTripMapItem | null): string {
    return [item?.trip?.tripStartTime, item?.trip?.tripEndTime].filter(Boolean).join(' - ');
  }

  getNextStopName(item: PassengerTripMapItem | null): string {
    return item?.nextStop?.stopName || 'Next stop unavailable';
  }

  getNextStopEta(item: PassengerTripMapItem | null): string {
    const nextStop = item?.nextStop;
    if (!nextStop) {
      return 'No next stop available';
    }

    if (nextStop.etaConfidence === 'unavailable-vehicle-stationary') {
      return nextStop.distanceKm !== undefined && nextStop.distanceKm !== null
        ? `${Number(nextStop.distanceKm).toFixed(1)} km away`
        : 'Vehicle is stationary';
    }

    if (nextStop.estimatedMinutes !== undefined && nextStop.estimatedMinutes !== null) {
      const minutes = Math.max(0, Math.round(Number(nextStop.estimatedMinutes)));
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return `${hours} hr${remainingMinutes ? ` ${remainingMinutes} min` : ''} ETA`;
      }
      return `${minutes} min ETA`;
    }

    return nextStop.distanceKm !== undefined && nextStop.distanceKm !== null
      ? `${Number(nextStop.distanceKm).toFixed(1)} km away`
      : 'ETA unavailable';
  }

  getOrderedStops(item: PassengerTripMapItem | null): PassengerRouteStop[] {
    return this.asArray<PassengerRouteStop>(item?.route?.stops)
      .sort((a, b) => Number(a.stopOrder || 0) - Number(b.stopOrder || 0));
  }

  getPassengerEmptyState(): string {
    if (!this.passengerTrips.length) {
      return 'You do not have booked trips to track yet.';
    }

    if (this.selectedPassengerTrip?.trip?.isPast && !this.asArray<VehicleTracking>(this.selectedPassengerTrip.routeHistory).length) {
      return 'This past trip has no travelled history yet. Showing the planned route.';
    }

    if (this.selectedPassengerTrip && !this.selectedPassengerTrip.currentTracking) {
      return 'Vehicle location has not reported yet. Showing booked route details.';
    }

    return '';
  }

  private hasValidCoordinates(tracking: VehicleTracking): boolean {
    const latitude = Number(tracking.vehicleTrackingLatitude);
    const longitude = Number(tracking.vehicleTrackingLongitude);

    return Number.isFinite(latitude)
      && Number.isFinite(longitude)
      && Math.abs(latitude) <= 90
      && Math.abs(longitude) <= 180;
  }

  private hasStopCoordinates(stop: PassengerRouteStop): boolean {
    const latitude = Number(stop.stopLatitude);
    const longitude = Number(stop.stopLongitude);
    return Number.isFinite(latitude)
      && Number.isFinite(longitude)
      && Math.abs(latitude) <= 90
      && Math.abs(longitude) <= 180;
  }

  private getLatLng(tracking: VehicleTracking): L.LatLngExpression {
    return [
      Number(tracking.vehicleTrackingLatitude),
      Number(tracking.vehicleTrackingLongitude)
    ];
  }

  private getTrackingStatus(tracking: VehicleTracking): VehicleMapStatus {
    const lastSeen = new Date(tracking.vehicleTrackingDateTime).getTime();
    const minutesSinceUpdate = Number.isNaN(lastSeen)
      ? Number.POSITIVE_INFINITY
      : (Date.now() - lastSeen) / 60000;

    if (minutesSinceUpdate > 10) {
      return 'stale';
    }

    return Number(tracking.vehicleTrackingSpeed || 0) > 5 ? 'moving' : 'idle';
  }

  private createVehicleIcon(status: VehicleMapStatus): L.DivIcon {
    const className = `vehicle-marker vehicle-marker-${status}`;

    return L.divIcon({
      className,
      html: '<span class="vehicle-marker-core"><i class="material-icons">directions_bus</i></span>',
      iconSize: [34, 34],
      iconAnchor: [17, 17],
      popupAnchor: [0, -18]
    });
  }

  private getDefaultPassengerTrip(trips: PassengerTripMapItem[]): PassengerTripMapItem | null {
    return trips.find(item => item.trip?.isActive && !item.trip?.isPast)
      || trips.find(item => !item.trip?.isPast)
      || trips[0]
      || null;
  }

  private mapPassengerVehicle(item: PassengerTripMapItem): any | null {
    if (!item.vehicle) {
      return null;
    }

    return {
      vehicleID: item.vehicle.vehicleID,
      vehicleLabel: item.vehicle.vehicleLabel,
      vehicleRegistration: item.vehicle.vehicleRegistration,
      vehicleIdentificationNumber: item.vehicle.vehicleIdentificationNumber,
      passengerTrip: item
    };
  }

  private createPassengerSummary(trips: PassengerTripMapItem[]) {
    const summary = {
      total: trips.length,
      moving: 0,
      idle: 0,
      stale: 0,
      noSignal: 0
    };

    trips.forEach(item => {
      if (!item.currentTracking || !this.hasValidCoordinates(item.currentTracking)) {
        summary.noSignal++;
        return;
      }
      summary[this.getTrackingStatus(item.currentTracking)]++;
    });

    return summary;
  }

  private clearMapLayers(): void {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
    this.vehicleMarkers.clear();
    this.vehicleStatuses.clear();
    this.clearRouteLayers();
  }

  private clearRouteLayers(): void {
    this.routeLayers.forEach(layer => layer.remove());
    this.routeLayers = [];
  }

  private getRouteLayerBounds(): L.LatLngBounds | null {
    const points: L.LatLng[] = [];
    this.routeLayers.forEach(layer => {
      if (layer instanceof L.Polyline) {
        const latLngs = layer.getLatLngs().flat(Number.POSITIVE_INFINITY) as L.LatLng[];
        points.push(...latLngs);
      }

      if (layer instanceof L.CircleMarker) {
        points.push(layer.getLatLng());
      }
    });

    return points.length ? L.latLngBounds(points) : null;
  }

  private asArray<T>(value: unknown): T[] {
    if (Array.isArray(value)) {
      return value as T[];
    }

    const collection = value as { $values?: T[] } | null | undefined;
    return Array.isArray(collection?.$values) ? collection.$values : [];
  }

  private syncFullscreenState = (): void => {
    this.isFullscreen = !!document.fullscreenElement;
    this.invalidateMapSize();
  };

  private invalidateMapSize(): void {
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 150);
  }
}
